// src/pages/markets/board/utils/entryMarkers.ts
//
// One dot per tracked position. The x is the position's age, the y is what
// they paid — averaged over every buy they made, which is why a trader who
// scaled in gets one dot at their average rather than a trail.

import type { TraderScore } from '../api/boardTypes';

export const MAX_ENTRY_MARKERS = 8;

export interface EntryMarker {
    address: string;
    /** One address can hold both sides, so this is half of the row's identity. */
    outcomeIndex: number;
    side: 'YES' | 'NO';
    /** What they paid, on their own side. */
    entryPrice: number;
    /** The same price on the first outcome's axis, which is what the line plots. */
    yesPrice: number;
    enteredAtMs: number;
    capitalUsd: number;
    heldSeconds: number;
    lastChangeAt: string | null;
}

export interface EntryMarkerSet {
    markers: EntryMarker[];
    /** Entered before the drawn window, so there is no honest x for them. */
    earlier: number;
    /** In-window but past the cap. */
    hidden: number;
}

export interface BuildEntryMarkersOptions {
    nowMs: number;
    windowStartMs: number;
    max?: number;
}

const EMPTY: EntryMarkerSet = { markers: [], earlier: 0, hidden: 0 };

function clamp01(v: number): number {
    return Math.max(0, Math.min(1, v));
}

export function buildEntryMarkers(
    traders: readonly TraderScore[],
    opts: BuildEntryMarkersOptions
): EntryMarkerSet {
    if (traders.length === 0) return EMPTY;

    const max = opts.max ?? MAX_ENTRY_MARKERS;
    const inWindow: EntryMarker[] = [];
    let earlier = 0;

    for (const t of traders) {
        if (t.avg_entry_price === null || t.position_age_seconds === null)
            continue;
        if (t.position_age_seconds < 0) continue;

        const enteredAtMs = opts.nowMs - t.position_age_seconds * 1000;
        if (enteredAtMs < opts.windowStartMs) {
            earlier += 1;
            continue;
        }

        const side = t.side === 'NO' ? 'NO' : 'YES';
        const entryPrice = clamp01(t.avg_entry_price);

        inWindow.push({
            address: t.address,
            outcomeIndex: t.outcome_index,
            side,
            entryPrice,
            yesPrice: side === 'NO' ? 1 - entryPrice : entryPrice,
            enteredAtMs,
            capitalUsd: Math.abs(t.cost_usd),
            heldSeconds: t.position_age_seconds,
            lastChangeAt: t.last_position_change_at,
        });
    }

    inWindow.sort((a, b) => b.capitalUsd - a.capitalUsd);

    return {
        markers: inWindow.slice(0, max),
        earlier,
        hidden: Math.max(0, inWindow.length - max),
    };
}

export function markerChipText(set: {
    earlier: number;
    hidden: number;
}): string | null {
    const parts: string[] = [];
    if (set.earlier > 0) parts.push(`+${set.earlier} earlier`);
    if (set.hidden > 0) parts.push(`+${set.hidden} not shown`);
    return parts.length === 0 ? null : parts.join(' · ');
}
